import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'ncoded-component-library';
import GrantAccessModal from '../GrantAccessModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import OrganizeRequestedFilesModal from '../OrganizeRequestedFilesModal';
import { DataRoomRFICategory } from 'router/subrouters/DataRoom/models/DataRoomRfi';

import './AutomaticallyAddFilesToDr.styles.scss';

type AutomaticallyAddFilesToDrProps = {
  className?: string;
  dataRoomRfiId: string;
  automaticFileUploadToDataRoom?: boolean;
  grantAccessDataRoomMemberIds: string[];
  dataRoomRFICategories: DataRoomRFICategory[];
};

const AutomaticallyAddFilesToDr: React.FC<AutomaticallyAddFilesToDrProps> = (
  props,
) => {
  const {
    className,
    dataRoomRfiId,
    grantAccessDataRoomMemberIds,
    automaticFileUploadToDataRoom,
    dataRoomRFICategories,
  } = props;

  const [grantAccessModal, grantAccessModalRef] = useCallbackRef<OverlayRef>();
  const [organizeFileModal, organizeFileModalRef] =
    useCallbackRef<OverlayRef>();

  const { popServerError } = useContext(PopNotificationsContext);
  const { updateRfi } = useContext(RfiContext);

  const classes = classNames('automatically-add-files-to-dr', className);

  const { t } = useTranslation();

  const optOutOfAutomaticUpload = useMemo(
    () => !automaticFileUploadToDataRoom,
    [automaticFileUploadToDataRoom],
  );

  const onOptOut = useCallback(
    (optOutOfAutomaticUpload: boolean) => {
      try {
        const automaticFileUploadToDataRoom = !optOutOfAutomaticUpload;

        updateRfi({ _id: dataRoomRfiId, automaticFileUploadToDataRoom });
        dataRoomApi.rfi.patchRfi(dataRoomRfiId, {
          automaticFileUploadToDataRoom,
        });
      } catch (e) {
        popServerError(e);
        updateRfi({
          _id: dataRoomRfiId,
          automaticFileUploadToDataRoom: optOutOfAutomaticUpload,
        });
      }
    },
    [dataRoomRfiId, updateRfi, popServerError],
  );

  return (
    <div className={classes}>
      <div>
        <p>
          {t('allFilesAutomaticallyUploaded')}{' '}
          <Trans
            i18nKey={'changeDestination'}
            components={{
              button: (
                <Button variant="link" onClick={organizeFileModal?.open} />
              ),
            }}
          />
          <OrganizeRequestedFilesModal
            ref={organizeFileModalRef}
            dataRoomRfiId={dataRoomRfiId}
            dataRoomRFICategories={dataRoomRFICategories}
          />
        </p>

        <Checkbox
          checked={optOutOfAutomaticUpload}
          label={t('optOutAutomaticFileUpload')}
          onChange={(value) => onOptOut(!!value)}
        />
      </div>

      <div>
        <p>
          <Trans
            i18nKey={'dataRoomMembersAccessNewFiles'}
            components={{
              button: (
                <Button variant="link" onClick={grantAccessModal?.open} />
              ),
            }}
          />
        </p>

        <GrantAccessModal
          ref={grantAccessModalRef}
          dataRoomRfiId={dataRoomRfiId}
          grantAccessDataRoomMemberIds={grantAccessDataRoomMemberIds}
          onSuccessfullyGranted={(grantAccessDataRoomMemberIds) =>
            updateRfi({
              _id: dataRoomRfiId,
              grantAccessDataRoomMemberIds,
            })
          }
        />
      </div>
    </div>
  );
};

export default AutomaticallyAddFilesToDr;
